import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { FaTimes } from "react-icons/fa";
import { AddPackageModalProps } from "../../Types/types";
import { uploadZipFile } from "../Manual_List/fetch/slices";
import CircularProgress from "@mui/material/CircularProgress";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export const AddPackageModal: React.FC<AddPackageModalProps> = ({
  isOpen,
  onClose,
  translations,
  values,
  setValues,
  onClick,
}) => {
  const dispatch = useDispatch();
  const [localValues, setLocalValues] = useState(values);
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string>("");
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setLocalValues(values);
    }
  }, [isOpen, values]);

  if (!isOpen) return null;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.name.endsWith(".zip")) {
      setFile(selectedFile);
      setFileError("");
    } else {
      setFileError(translations.invalidZipFile);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    const selectedFile = e.dataTransfer.files?.[0];
    if (selectedFile && selectedFile.name.endsWith(".zip")) {
      setFile(selectedFile);
      setFileError("");
    } else {
      setFileError(translations.invalidZipFile);
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleRemoveFile = () => {
    setFile(null);
  };

  const handleSave = () => {
    setValues(localValues);

    if (file) {
      setIsUploading(true);
      dispatch(uploadZipFile(file) as any)
        .then((response: any) => {
          setIsUploading(false);
          if (response.error) {
            toast.error(translations.uploadFailed);
          } else {
            toast.success(translations.uploadSuccess);
            onClose();
          }
        })
        .catch((error: unknown) => {
          setIsUploading(false);
          console.error("Upload error:", error);
          toast.error(translations.uploadFailed);
        });
    } else {
      setFileError(translations.uploadBeforeSave);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 px-12 bg-[#586EDFCC]">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-7xl relative mx-2 sm:mx-auto dark:bg-darkSecondaryBackground flex flex-col">
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-gray-500 z-10"
        >
          <FaTimes />
        </button>

        <div className="flex justify-center items-center p-4 pb-2">
          <h2 className="text-lg font-semibold">{translations.addPackage}</h2>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-2">
          <form className="space-y-3">
            <div className="mb-4">
              <label htmlFor="fileUpload" className="block mb-1 text-base ml-2">
                {translations.uploadZipFile}
              </label>
              <div
                className={`w-full p-8 border-2 border-dashed rounded-lg shadow-sm transition-colors ${
                  dragActive
                    ? "border-purple bg-purple bg-opacity-5 dark:bg-purple dark:bg-opacity-10"
                    : "border-gray-300 dark:bg-darkSecondaryBackground dark:border-gray-600 hover:border-gray-400"
                }`}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  id="fileUpload"
                  className="hidden"
                  accept=".zip"
                  onChange={handleFileChange}
                />
                {file ? (
                  <div className="relative w-full min-h-[80px] flex items-center justify-center">
                    <p className="text-center font-medium text-purple">
                      {file.name}
                    </p>
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 bg-white dark:bg-darkSecondaryBackground rounded-full p-1.5 shadow hover:bg-red-50 dark:hover:bg-red-900"
                      onClick={handleRemoveFile}
                    >
                      <FaTimes className="text-red-500" />
                    </button>
                  </div>
                ) : (
                  <label
                    htmlFor="fileUpload"
                    className="w-full h-full flex items-center justify-center cursor-pointer min-h-[120px]"
                  >
                    <p className="text-center text-gray-600 dark:text-gray-400">
                      {translations.dragDropOrClick}
                    </p>
                  </label>
                )}
              </div>
              {fileError && (
                <span className="text-red-500 font-semibold text-sm">
                  {fileError}
                </span>
              )}
            </div>
          </form>
          {isUploading && (
            <div className="flex justify-center items-center mt-4">
              <CircularProgress />
            </div>
          )}
        </div>

        <div className="flex justify-between items-center space-x-2 p-4 pt-2">
          <button
            type="button"
            className="bg-purple text-white px-4 py-2 rounded-lg font-medium shadow-sm hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={onClick}
            disabled={isUploading}
          >
            {translations.addIndividually}
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg font-medium shadow-sm dark:bg-white dark:text-black hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={onClose}
              disabled={isUploading}
            >
              {translations.discard}
            </button>
            <button
              type="button"
              className="bg-purple text-white px-4 py-2 rounded-lg font-medium shadow-sm hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleSave}
              disabled={isUploading}
            >
              {translations.save}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
